import React, { useState, useEffect } from 'react';
import { Trash2, Flag, X, AlertCircle, Download } from 'lucide-react';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const PaymentLogsViewer = ({ isOpen, onClose, onUpdate }) => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [flagModal, setFlagModal] = useState({ isOpen: false, payment: null });
  const [flagNote, setFlagNote] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadPayments();
    }
  }, [isOpen]);

  const loadPayments = async () => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem('token');
      console.log('Loading manual payments...');
      const response = await axios.get(`${API_URL}/payments/manual-payments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Manual payments response:', response.data);
      setPayments(response.data.payments || []);
    } catch (error) {
      console.error('Load payments error:', error);
      console.error('Error details:', error.response?.data);
      toast.error(error.response?.data?.error || 'Failed to load payment entries');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (payment) => {
    const confirmMsg = payment.payment_source === 'online'
      ? 'This will remove the payment record from this appointment. The appointment will remain but show as unpaid. Continue?'
      : 'Are you sure you want to delete this payment entry? This cannot be undone.';

    console.log('Attempting to delete payment:', payment.id);
    if (!window.confirm(confirmMsg)) {
      return;
    }

    try {
      const token = sessionStorage.getItem('token');
      console.log('Deleting payment with ID:', payment.id, 'Source:', payment.payment_source);
      const response = await axios.delete(
        `${API_URL}/payments/manual-payment/${payment.id}?source=${payment.payment_source}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('Delete response:', response.data);
      
      toast.success('Payment entry deleted');
      loadPayments();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Delete payment error:', error);
      console.error('Error details:', error.response?.data);
      toast.error(error.response?.data?.error || 'Failed to delete payment entry');
    }
  };

  const handleExport = () => {
    if (payments.length === 0) {
      toast.error('No payments to export');
      return;
    }

    // Create CSV content
    const headers = ['Date', 'Client', 'Service', 'Amount', 'Method', 'Transaction ID', 'Source', 'Recorded By', 'Flagged', 'Notes'];
    const rows = payments.map(p => [
      format(new Date(p.payment_date), 'yyyy-MM-dd'),
      p.client_name || '',
      p.service_name || '',
      p.amount,
      p.payment_method,
      p.transaction_id || '',
      p.payment_source,
      p.recorded_by_name || '',
      p.is_flagged ? 'Yes' : 'No',
      (p.notes || '').replace(/"/g, '""') // Escape quotes
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `payment-history-${format(new Date(), 'yyyy-MM-dd')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success('Payment history exported');
  };

  const openFlagModal = (payment) => {
    setFlagModal({ isOpen: true, payment });
    setFlagNote(payment.flag_note || '');
  };

  const handleFlag = async () => {
    console.log('Flagging payment:', flagModal.payment.id);
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.patch(
        `${API_URL}/payments/manual-payment/${flagModal.payment.id}/flag`,
        {
          is_flagged: !flagModal.payment.is_flagged,
          flag_note: flagNote
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('Flag response:', response.data);
      
      toast.success(flagModal.payment.is_flagged ? 'Flag removed' : 'Payment flagged');
      setFlagModal({ isOpen: false, payment: null });
      setFlagNote('');
      loadPayments();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Flag payment error:', error);
      console.error('Error details:', error.response?.data);
      toast.error(error.response?.data?.error || 'Failed to update flag');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          <div className="flex items-center justify-between p-6 border-b">
            <h2 className="text-xl font-bold">Payment History</h2>
            <div className="flex items-center space-x-3">
              <button
                onClick={handleExport}
                className="btn btn-secondary flex items-center space-x-2"
                disabled={payments.length === 0}
              >
                <Download size={18} />
                <span>Export CSV</span>
              </button>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
              </div>
            ) : payments.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-600">No manual payment entries found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50">
                      <th className="text-left py-3 px-4 font-semibold">Date</th>
                      <th className="text-left py-3 px-4 font-semibold">Client</th>
                      <th className="text-left py-3 px-4 font-semibold">Service</th>
                      <th className="text-right py-3 px-4 font-semibold">Amount</th>
                      <th className="text-left py-3 px-4 font-semibold">Method</th>
                      <th className="text-left py-3 px-4 font-semibold">Source</th>
                      <th className="text-left py-3 px-4 font-semibold">Transaction ID</th>
                      <th className="text-center py-3 px-4 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((payment) => (
                      <tr 
                        key={payment.id} 
                        className={`border-b border-gray-100 hover:bg-gray-50 ${
                          payment.is_flagged ? 'bg-yellow-50' : ''
                        }`}
                      >
                        <td className="py-3 px-4">
                          <div className="flex items-center space-x-2">
                            {payment.is_flagged && (
                              <Flag size={16} className="text-yellow-600 fill-yellow-600" />
                            )}
                            <span>{format(new Date(payment.payment_date), 'MMM d, yyyy')}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4">{payment.client_name || '-'}</td>
                        <td className="py-3 px-4">{payment.service_name || '-'}</td>
                        <td className="text-right py-3 px-4 font-semibold">
                          {formatCurrency(payment.amount)}
                        </td>
                        <td className="py-3 px-4 capitalize">{payment.payment_method}</td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            payment.payment_source === 'online' 
                              ? 'bg-blue-100 text-blue-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {payment.payment_source === 'online' ? 'Online' : 'Manual'}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">
                          {payment.transaction_id || '-'}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center justify-center space-x-2">
                            <button
                              onClick={() => openFlagModal(payment)}
                              className={`p-1 rounded hover:bg-gray-200 ${
                                payment.is_flagged ? 'text-yellow-600' : 'text-gray-600'
                              }`}
                              title={payment.is_flagged ? 'Unflag' : 'Flag as important'}
                            >
                              <Flag size={18} className={payment.is_flagged ? 'fill-yellow-600' : ''} />
                            </button>
                            <button
                              onClick={() => handleDelete(payment)}
                              className="p-1 text-red-600 rounded hover:bg-red-50"
                              title="Delete payment entry"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Notes Section */}
            {!loading && payments.some(p => p.notes) && (
              <div className="mt-6">
                <h3 className="font-semibold mb-3">Payment Notes:</h3>
                <div className="space-y-2">
                  {payments.filter(p => p.notes).map((payment) => (
                    <div key={payment.id} className="p-3 bg-gray-50 rounded-lg text-sm">
                      <span className="font-medium">
                        {format(new Date(payment.payment_date), 'MMM d, yyyy')} - {formatCurrency(payment.amount)}:
                      </span>
                      <p className="text-gray-600 mt-1">{payment.notes}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="p-6 border-t bg-gray-50">
            <button
              onClick={onClose}
              className="btn btn-secondary w-full"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Flag Modal */}
      {flagModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="flex items-center justify-between p-6 border-b">
              <div className="flex items-center space-x-2">
                <Flag className="text-yellow-600" size={24} />
                <h3 className="text-lg font-bold">
                  {flagModal.payment.is_flagged ? 'Remove Flag' : 'Flag Payment'}
                </h3>
              </div>
              <button
                onClick={() => setFlagModal({ isOpen: false, payment: null })}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6">
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">Payment Details:</p>
                <p className="font-semibold">
                  {formatCurrency(flagModal.payment.amount)} - {flagModal.payment.payment_method}
                </p>
                <p className="text-sm text-gray-600">
                  {format(new Date(flagModal.payment.payment_date), 'MMM d, yyyy')}
                </p>
              </div>

              {!flagModal.payment.is_flagged && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Flag Note (Optional)
                  </label>
                  <textarea
                    value={flagNote}
                    onChange={(e) => setFlagNote(e.target.value)}
                    className="input"
                    rows="3"
                    placeholder="Why is this payment important?"
                  />
                </div>
              )}

              {flagModal.payment.is_flagged && flagModal.payment.flag_note && (
                <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-start space-x-2">
                    <AlertCircle className="text-yellow-600 flex-shrink-0 mt-0.5" size={18} />
                    <div>
                      <p className="text-sm font-medium text-yellow-800">Current Flag Note:</p>
                      <p className="text-sm text-yellow-700 mt-1">{flagModal.payment.flag_note}</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex space-x-3">
                <button
                  onClick={() => setFlagModal({ isOpen: false, payment: null })}
                  className="btn btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  onClick={handleFlag}
                  className={`btn flex-1 ${
                    flagModal.payment.is_flagged ? 'btn-secondary' : 'btn-primary'
                  }`}
                >
                  {flagModal.payment.is_flagged ? 'Remove Flag' : 'Flag Payment'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PaymentLogsViewer;
